import React, { useEffect, useRef, useState } from 'react';
import { FormElement } from '../../schema/form.schema';
import styles from '../../assets/css/collapsible.module.css';
import FormElementComponent from '../form/FormElement';
import { FormDataValue } from '../../hooks/useFormData';
import { SwitchableOption } from '../../schema/switchable.schema';
import ChevronIcon from '../choice/ChevronIcon';

interface IProps {
	currentOption: string;
	isOpen: boolean;
	header: string | React.ReactNode;
	children: Array<FormElement>;
	index: number;
	setOpenIndex: (index: number | null) => void;
	onChange: (key: string, value: FormDataValue) => void;
	onValidationChange: (key: string, value: boolean) => void;
	handleChange: (value: string) => void;
	options: Array<SwitchableOption>;
	option: SwitchableOption;
}

const Collapsible: React.FC<IProps> = ({
	isOpen,
	header,
	children,
	index,
	setOpenIndex,
	onChange,
	onValidationChange,
	handleChange,
	option,
	options
}) => {
	const [height, setHeight] = useState<number | undefined>(isOpen ? undefined : 0);
	const ref = useRef<HTMLDivElement>(null);

	const handleFilterOpening = () => {
		setOpenIndex(isOpen ? null : index);
		const selectedCollapsible = options.find((collapsibleElement) => collapsibleElement.value === option.value);
		if (selectedCollapsible) handleChange(selectedCollapsible.value);
	};

	useEffect(() => {
		if (!height || !isOpen || !ref.current) return undefined;
		const resizeObserver = new ResizeObserver((el) => {
			setHeight(el[0].contentRect.height);
		});
		resizeObserver.observe(ref.current);
		return () => {
			resizeObserver.disconnect();
		};
	}, [height, isOpen]);

	useEffect(() => {
		if (isOpen) setHeight(ref.current?.getBoundingClientRect().height);
		else setHeight(0);
	}, [isOpen]);

	return (
		<div style={{ width: '100%' }}>
			<div className={styles['collapsible-card']}>
				<div>
					<div className={styles['collapsible-header']}>
						<div className={styles['collapsible-title-text']}>{header}</div>
						<button
							type="button"
							className={`${styles['collapsible-icon-button']} ${isOpen ? styles.rotated : ''}`}
							onClick={handleFilterOpening}
						>
							<ChevronIcon />
						</button>
					</div>
				</div>
				<div className={styles['collapsible-content']} style={{ height }}>
					<div ref={ref}>
						<div className={styles['collapsible-content-padding']}>
							{children.map((element, index) => (
								<div key={index}>
									{' '}
									<FormElementComponent
										element={element}
										onChange={onChange}
										onValidationChange={onValidationChange}
									/>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

interface CollapsibleComponentProps {
	openIndex: number | null;
	currentOption: string;
	setOpenIndex: (index: number | null) => void;
	onChange: (key: string, value: FormDataValue) => void;
	onValidationChange: (key: string, value: boolean) => void;
	handleChange: (value: string) => void;
	options: Array<SwitchableOption>;
}

const CollapsibleMap: React.FC<CollapsibleComponentProps> = ({
	currentOption,
	options,
	openIndex,
	setOpenIndex,
	onValidationChange,
	onChange,
	handleChange
}) => {
	return (
		<div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
			{options.map((option, index) => (
				<Collapsible
					currentOption={currentOption}
					isOpen={index === openIndex}
					header={option.value}
					key={index}
					index={index}
					setOpenIndex={setOpenIndex}
					onValidationChange={onValidationChange}
					onChange={onChange}
					handleChange={handleChange}
					options={options}
					option={option}
				>
					{option.content}
				</Collapsible>
			))}
		</div>
	);
};

export default CollapsibleMap;
